import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Plus, Pencil, Trash2, BookOpen, Loader2, X, Crown, Sparkles, ListPlus, Check, Play, Users2, Undo2, RotateCcw, ChevronDown, ChevronUp, ArrowLeftRight, GripVertical, Camera } from "lucide-react";
import axios from "axios";
import StarCanvas from "@/components/StarCanvas";
import ManagePlan from "@/components/ManagePlan";
import VerifyBanner from "@/components/VerifyBanner";
import { LIBRARY } from "@/constants/testIds";
import useActiveDraft from "@/hooks/useActiveDraft";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = [
  { value: "enchanted_forest", label: "Enchanted Forest" },
  { value: "space_adventure", label: "Space Adventure" },
  { value: "ocean_kingdom", label: "Ocean Kingdom" },
  { value: "dragon_castle", label: "Dragon Castle" },
  { value: "fairy_garden", label: "Fairy Garden" },
  { value: "time_travel", label: "Time Travel" },
  { value: "candy_cloud", label: "Candy Cloud World" },
  { value: "moon_village", label: "Moon Village" },
  { value: "dinosaur_valley", label: "Dinosaur Valley" },
  { value: "superhero_city", label: "Superhero City" },
  { value: "pirate_cove", label: "Pirate Cove" },
];

const themeLabel = (t) =>
  t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Bedtime Adventure";

function ProfileForm({ initial, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(
    initial || { name: "", age: 5, favorite_theme: "enchanted_forest" }
  );
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoTooBig, setPhotoTooBig] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!photoFile) return;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const existingPhotoUrl = initial?.has_photo ? `${API}/profiles/${initial.profile_id}/photo` : null;

  const handlePickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoTooBig(f.size > 5 * 1024 * 1024);
    setPhotoFile(f);
  };

  return (
    <motion.form
      data-testid={LIBRARY.profileForm}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={(e) => { e.preventDefault(); if (!photoTooBig) onSave(form, photoFile); }}
      className="glass-card rounded-xl p-5 mt-4 space-y-4"
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          data-testid="profile-photo-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          className="relative w-16 h-16 rounded-full overflow-hidden shrink-0 border-2 border-dashed border-purple-500/30 hover:border-amber-400/50 transition-colors flex items-center justify-center bg-white/5 group"
        >
          {photoPreview || existingPhotoUrl ? (
            <img src={photoPreview || existingPhotoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <Camera size={18} className="text-slate-500 group-hover:text-amber-400 transition-colors" />
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
            <Camera size={14} className="text-white" />
          </div>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handlePickPhoto}
          data-testid="profile-photo-input"
        />
        <p className="text-xs text-slate-500 leading-relaxed">
          Optional: add a photo so AI illustrations can resemble {form.name || "your child"}. Never shared, used only for their stories.
        </p>
      </div>
      {photoTooBig && <p data-testid="profile-photo-error" className="text-xs text-red-400">Photo must be under 5MB — please pick a smaller image.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">Child&apos;s Name</label>
          <input
            data-testid={LIBRARY.profileNameInput}
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Lily"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">
            Age: <span className="text-amber-400 font-bold">{form.age}</span>
          </label>
          <input
            type="range" min={3} max={10} step={1}
            value={form.age}
            onChange={(e) => setForm((f) => ({ ...f, age: parseInt(e.target.value) }))}
            className="w-full audio-progress h-2 cursor-pointer mt-3"
            style={{ background: `linear-gradient(to right, #F59E0B ${((form.age - 3) / 7) * 100}%, rgba(255,255,255,0.1) 0%)` }}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">Favorite Theme</label>
          <select
            data-testid="profile-theme-select"
            value={form.favorite_theme || "enchanted_forest"}
            onChange={(e) => setForm((f) => ({ ...f, favorite_theme: e.target.value }))}
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-purple-500/20 text-white text-sm focus:outline-none focus:border-amber-400/50 appearance-none cursor-pointer"
          >
            {THEMES.map((t) => (
              <option key={t.value} value={t.value} style={{ background: "#1a1535" }}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          data-testid={LIBRARY.profileSaveBtn}
          type="submit"
          disabled={saving || !form.name.trim() || photoTooBig}
          className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-2"
        >
          {saving && <Loader2 size={12} className="animate-spin" />}
          {initial?.profile_id ? "Save Changes" : "Add Child"}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-400 hover:text-white">Cancel</button>
      </div>
    </motion.form>
  );
}

export default function MyStories() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const draft = useActiveDraft(user);
  const [stories, setStories] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [upsell, setUpsell] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [playlistsList, setPlaylistsList] = useState([]);
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const [newPlName, setNewPlName] = useState("");
  const [plMenuError, setPlMenuError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // story object
  const [deletingId, setDeletingId] = useState(null);
  const [siblingPickMode, setSiblingPickMode] = useState(false);
  const [siblingPicks, setSiblingPicks] = useState([]); // [profile, profile]
  const [trash, setTrash] = useState([]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [undoStory, setUndoStory] = useState(null); // {story_id, title, purge_at}
  const [undoTimer, setUndoTimer] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingId, setPurgingId] = useState(null);
  const [dragSlot, setDragSlot] = useState(null); // "hero" | "sibling" | null
  const [trashError, setTrashError] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
      const [st, pr, pl, tr] = await Promise.all([
        axios.get(`${API}/stories`, { withCredentials: true }),
        axios.get(`${API}/profiles`, { withCredentials: true }),
        axios.get(`${API}/playlists`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/stories/trash/list`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStories(st.data);
      setProfiles(pr.data);
      setPlaylistsList(pl.data);
      setTrash(tr.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Clear any pending undo-toast timer if the page unmounts before it fires
  useEffect(() => () => { if (undoTimer) clearTimeout(undoTimer); }, [undoTimer]);

  const handleSaveProfile = async (form, photoFile) => {
    setSaving(true);
    setFormError("");
    try {
      let profileData;
      if (editing?.profile_id) {
        const { data } = await axios.put(`${API}/profiles/${editing.profile_id}`, form, { withCredentials: true });
        profileData = data;
      } else {
        const { data } = await axios.post(`${API}/profiles`, form, { withCredentials: true });
        profileData = data;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await axios.post(`${API}/profiles/${profileData.profile_id}/photo`, fd, { withCredentials: true });
        profileData = { ...profileData, has_photo: true };
      }
      if (editing?.profile_id) {
        setProfiles((p) => p.map((x) => (x.profile_id === profileData.profile_id ? profileData : x)));
      } else {
        setProfiles((p) => [...p, profileData]);
      }
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      if (err.response?.status === 402) {
        setUpsell(true);
        setShowForm(false);
      } else {
        setFormError(err.response?.data?.detail || "Could not save profile");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async (profile) => {
    if (!window.confirm(`Remove ${profile.name}'s profile? Their saved stories will stay in your library.`)) return;
    setDeleteError("");
    try {
      await axios.delete(`${API}/profiles/${profile.profile_id}`, { withCredentials: true });
      setProfiles((p) => p.filter((x) => x.profile_id !== profile.profile_id));
      if (filter === profile.profile_id) setFilter("all");
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Could not delete profile. Please try again.");
    }
  };

  const toggleInPlaylist = async (pl, storyId) => {
    const ids = pl.story_ids.includes(storyId)
      ? pl.story_ids.filter((id) => id !== storyId)
      : [...pl.story_ids, storyId];
    setPlaylistsList((pls) => pls.map((p) => (p.playlist_id === pl.playlist_id ? { ...p, story_ids: ids } : p)));
    try {
      await axios.put(`${API}/playlists/${pl.playlist_id}`, { story_ids: ids }, { withCredentials: true });
    } catch (err) {
      setPlaylistsList((pls) => pls.map((p) => (p.playlist_id === pl.playlist_id ? { ...p, story_ids: pl.story_ids } : p)));
      setPlMenuError(err.response?.data?.detail || "Could not update playlist");
    }
  };

  const createPlaylistWithStory = async (e, storyId) => {
    e.preventDefault();
    const name = newPlName.trim();
    if (!name) return;
    setPlMenuError("");
    try {
      const { data } = await axios.post(`${API}/playlists`, { name }, { withCredentials: true });
      await axios.put(`${API}/playlists/${data.playlist_id}`, { story_ids: [storyId] }, { withCredentials: true });
      setPlaylistsList((pls) => [...pls, { ...data, story_ids: [storyId] }]);
      setNewPlName("");
    } catch (err) {
      setPlMenuError(err.response?.data?.detail || "Could not create playlist");
    }
  };

  const inAnyPlaylist = (storyId) => playlistsList.some((p) => p.story_ids.includes(storyId));
  const queuedTotal = playlistsList.reduce((n, p) => n + p.story_ids.length, 0);
  const firstNonEmpty = playlistsList.find((p) => p.story_ids.length > 0);

  const handleDeleteStory = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.story_id;
    const title = confirmDelete.title;
    const storyCopy = stories.find((s) => s.story_id === id);
    setDeletingId(id);
    setDeleteError("");
    try {
      const { data } = await axios.delete(`${API}/stories/${id}`, { withCredentials: true });
      // Optimistically move the story into the trash column
      setStories((prev) => prev.filter((s) => s.story_id !== id));
      setPlaylistsList((pls) => pls.map((p) => ({ ...p, story_ids: p.story_ids.filter((sid) => sid !== id) })));
      setTrash((prev) => [
        { ...(storyCopy || confirmDelete), deleted_at: new Date().toISOString(), purge_at: data.purge_at },
        ...prev,
      ]);
      setConfirmDelete(null);
      // Show undo toast for 8 seconds
      if (undoTimer) clearTimeout(undoTimer);
      setUndoStory({ story_id: id, title, purge_at: data.purge_at });
      const t = setTimeout(() => setUndoStory(null), 8000);
      setUndoTimer(t);
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Could not delete story. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (storyId) => {
    setRestoringId(storyId);
    setTrashError("");
    try {
      await axios.post(`${API}/stories/${storyId}/restore`, {}, { withCredentials: true });
      const restored = trash.find((s) => s.story_id === storyId);
      setTrash((prev) => prev.filter((s) => s.story_id !== storyId));
      if (restored) {
        const clean = { ...restored };
        delete clean.deleted_at;
        delete clean.purge_at;
        setStories((prev) => [clean, ...prev].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
      } else {
        const { data } = await axios.get(`${API}/stories`, { withCredentials: true });
        setStories(data);
      }
      if (undoStory?.story_id === storyId) {
        setUndoStory(null);
        if (undoTimer) clearTimeout(undoTimer);
      }
    } catch (err) {
      setTrashError(err.response?.data?.detail || "Could not restore. Please try again.");
    } finally {
      setRestoringId(null);
    }
  };

  const handlePurge = async (storyId) => {
    if (!window.confirm("Permanently delete this story? This cannot be undone.")) return;
    setPurgingId(storyId);
    setTrashError("");
    try {
      await axios.delete(`${API}/stories/${storyId}/purge`, { withCredentials: true });
      setTrash((prev) => prev.filter((s) => s.story_id !== storyId));
    } catch (err) {
      setTrashError(err.response?.data?.detail || "Could not permanently delete. Please try again.");
    } finally {
      setPurgingId(null);
    }
  };

  const daysLeft = (purgeAt) => {
    if (!purgeAt) return "";
    const ms = new Date(purgeAt).getTime() - Date.now();
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) return "expiring";
    if (days === 1) return "1 day left";
    return `${days} days left`;
  };

  const handleSiblingChipTap = (p) => {
    if (siblingPicks.find((x) => x.profile_id === p.profile_id)) {
      setSiblingPicks((prev) => prev.filter((x) => x.profile_id !== p.profile_id));
      return;
    }
    if (siblingPicks.length >= 2) return; // full — user must swap or remove
    setSiblingPicks((prev) => [...prev, p]);
  };

  const swapSiblingPicks = () => setSiblingPicks((prev) => (prev.length === 2 ? [prev[1], prev[0]] : prev));

  const dropOnSlot = (targetIdx) => {
    if (dragSlot === null || siblingPicks.length !== 2) return;
    const srcIdx = dragSlot === "hero" ? 0 : 1;
    if (srcIdx === targetIdx) { setDragSlot(null); return; }
    setSiblingPicks((prev) => {
      const next = [...prev];
      [next[srcIdx], next[targetIdx]] = [next[targetIdx], next[srcIdx]];
      return next;
    });
    setDragSlot(null);
  };

  const launchSiblingAdventure = () => {
    if (siblingPicks.length !== 2) return;
    const [hero, sib] = siblingPicks;
    const qs = new URLSearchParams({
      hero: hero.name,
      heroAge: String(hero.age),
      sibling: sib.name,
      siblingAge: String(sib.age),
      theme: hero.favorite_theme || "enchanted_forest",
    }).toString();
    navigate(`/?${qs}#generator`);
  };

  const matchesProfile = (story, profile) =>
    story.profile_id === profile.profile_id ||
    (story.child_name || "").toLowerCase() === profile.name.toLowerCase();

  const filteredStories =
    filter === "all"
      ? stories
      : stories.filter((s) => {
          const p = profiles.find((x) => x.profile_id === filter);
          return p ? matchesProfile(s, p) : true;
        });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A]">
        <Loader2 size={32} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A] px-4">
        <StarCanvas />
        <div className="relative z-10 glass-card rounded-3xl p-12 max-w-md w-full text-center">
          <BookOpen size={40} className="text-purple-400 mx-auto mb-6" />
          <h2 className="font-serif text-2xl font-bold text-white mb-2">Sign in to see your library</h2>
          <p className="text-slate-400 text-sm mb-6">Your saved bedtime stories live here once you&apos;re signed in.</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm"
          >
            Go to DreamWeaver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={LIBRARY.page} className="relative min-h-screen bg-[#0B091A] overflow-x-clip">
      <StarCanvas />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pb-24">
        {openMenuFor && (
          <div className="fixed inset-0 z-30" onClick={() => setOpenMenuFor(null)} />
        )}
        {/* Header bar */}
        <header className="flex items-center justify-between py-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Moon size={14} className="text-white" />
            </div>
            <span className="font-serif font-bold text-lg tracking-tight gradient-text">DreamWeaver</span>
          </Link>
          <div className="flex items-center gap-3">
            {draft && (
              <button
                data-testid="library-draft-badge"
                onClick={() => navigate(`/story/${draft.storyId}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/30 transition-colors"
              >
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Story in progress</span>
              </button>
            )}
            <Link
              to="/#generator"
              className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all flex items-center gap-2"
            >
              <Sparkles size={13} /> New Story
            </Link>
          </div>
        </header>

        {/* Page title */}
        <div className="mt-6 mb-12">
          <VerifyBanner user={user} />
          <div className="mt-8">
            <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80">Story Library</span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-3">
              Every adventure,<br /><span className="gradient-text">saved forever</span>
            </h1>
          </div>
        </div>

        {/* Children profiles */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-slate-400 tracking-wider uppercase">Your Children</h2>
            <div className="flex items-center gap-2">
              {profiles.length >= 2 && (
                <button
                  data-testid="sibling-pick-toggle"
                  onClick={() => { setSiblingPickMode((v) => !v); setSiblingPicks([]); }}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    siblingPickMode
                      ? "bg-amber-500/20 border border-amber-400/50 text-amber-300"
                      : "border border-purple-500/25 text-slate-300 hover:text-amber-300 hover:border-amber-400/40"
                  }`}
                >
                  <Users2 size={12} /> Sibling Adventure
                </button>
              )}
              <button
                data-testid={LIBRARY.addProfileBtn}
                onClick={() => { setEditing(null); setShowForm(true); setUpsell(false); }}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-amber-400/30 text-amber-300 text-xs font-medium hover:bg-amber-500/10 transition-colors"
              >
                <Plus size={13} /> Add Child
              </button>
            </div>
          </div>

          {siblingPickMode && (
            <div data-testid="sibling-pick-banner" className="mb-4 p-3 rounded-xl bg-amber-500/5 border border-amber-400/25 flex items-center gap-2 text-xs text-amber-200">
              <Users2 size={13} />
              {siblingPicks.length === 0
                ? "Tap two children — the first is the hero, the second is their sibling."
                : siblingPicks.length === 1
                ? `Great! Tap one more child to pair with ${siblingPicks[0].name}.`
                : "Perfect — drag the tiles or tap Swap to reorder who's the hero."}
              <button
                onClick={() => { setSiblingPickMode(false); setSiblingPicks([]); }}
                className="ml-auto text-slate-400 hover:text-white"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {siblingPickMode && siblingPicks.length === 2 && (
            <div data-testid="sibling-pair-preview" className="mb-5 glass-card rounded-2xl p-4">
              <div className="flex items-center gap-3">
                {[0, 1].map((idx) => {
                  const p = siblingPicks[idx];
                  const isHero = idx === 0;
                  return (
                    <div
                      key={idx}
                      data-testid={isHero ? "sibling-slot-hero" : "sibling-slot-sibling"}
                      draggable
                      onDragStart={() => setDragSlot(isHero ? "hero" : "sibling")}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropOnSlot(idx)}
                      onDragEnd={() => setDragSlot(null)}
                      className={`flex-1 flex items-center gap-3 p-3 rounded-xl bg-white/5 border transition-all cursor-grab active:cursor-grabbing ${
                        dragSlot ? "border-amber-400/60 ring-1 ring-amber-400/30" : "border-purple-500/25"
                      }`}
                    >
                      <GripVertical size={14} className="text-slate-500 shrink-0" />
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center text-white font-serif font-bold text-sm shrink-0">
                        {p.has_photo ? (
                          <img src={`${API}/profiles/${p.profile_id}/photo`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          p.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300/80">{isHero ? "Hero" : "Sibling"}</p>
                        <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                        <p className="text-[11px] text-slate-500">{p.age} yrs</p>
                      </div>
                    </div>
                  );
                })}
                <button
                  data-testid="sibling-swap-btn"
                  onClick={swapSiblingPicks}
                  title="Swap hero and sibling"
                  className="p-2.5 rounded-full border border-purple-500/30 text-slate-300 hover:text-amber-300 hover:border-amber-400/50 transition-colors"
                >
                  <ArrowLeftRight size={14} />
                </button>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  data-testid="sibling-clear-btn"
                  onClick={() => setSiblingPicks([])}
                  className="text-xs text-slate-400 hover:text-white px-3 py-2"
                >
                  Clear
                </button>
                <button
                  data-testid="sibling-launch-btn"
                  onClick={launchSiblingAdventure}
                  className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-xs font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                >
                  <Sparkles size={12} /> Launch Adventure
                </button>
              </div>
            </div>
          )}

          {profiles.length === 0 && !showForm && (
            <p className="text-sm text-slate-500">
              Add a child profile to pre-fill the story generator and keep each child&apos;s stories organized.
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            {profiles.map((p) => {
              const pickIdx = siblingPicks.findIndex((x) => x.profile_id === p.profile_id);
              return (
              <div
                key={p.profile_id}
                data-testid={`${LIBRARY.profileChip}-${p.profile_id}`}
                onClick={() => siblingPickMode && handleSiblingChipTap(p)}
                className={`glass-card rounded-xl px-4 py-3 flex items-center gap-3 transition-all ${
                  siblingPickMode
                    ? `cursor-pointer ${pickIdx >= 0 ? "border-amber-400/60 ring-2 ring-amber-400/40 bg-amber-500/10" : "hover:border-amber-400/30"}`
                    : ""
                }`}
              >
                <div className={`w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center text-white font-serif font-bold text-sm relative ${pickIdx >= 0 ? "ring-2 ring-amber-300" : ""}`}>
                  {p.has_photo ? (
                    <img src={`${API}/profiles/${p.profile_id}/photo`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    p.name.charAt(0).toUpperCase()
                  )}
                  {pickIdx >= 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center">
                      {pickIdx + 1}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.age} yrs · {themeLabel(p.favorite_theme)}</p>
                </div>
                {!siblingPickMode && (
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => { setEditing(p); setShowForm(true); }}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-amber-300 transition-colors"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      data-testid={`${LIBRARY.profileDeleteBtn}-${p.profile_id}`}
                      onClick={() => handleDeleteProfile(p)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {deleteError && <p className="mt-3 text-xs text-red-400">{deleteError}</p>}

          <AnimatePresence>
            {showForm && (
              <ProfileForm
                key={editing?.profile_id || "new"}
                initial={editing}
                onSave={handleSaveProfile}
                onCancel={() => { setShowForm(false); setEditing(null); setFormError(""); }}
                saving={saving}
                error={formError}
              />
            )}
          </AnimatePresence>

          {upsell && (
            <div className="mt-4 p-4 rounded-xl bg-amber-500/10 border border-amber-400/20 flex items-start gap-3">
              <Crown size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-300 font-medium">Free accounts include 1 child profile</p>
                <p className="text-xs text-slate-400 mt-0.5">Upgrade to Premium for unlimited profiles — perfect for siblings.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link to="/#pricing" className="px-4 py-1.5 rounded-full bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors whitespace-nowrap">
                  Upgrade — $7/mo
                </Link>
                <button onClick={() => setUpsell(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
              </div>
            </div>
          )}
        </section>

        {/* Filter tabs */}
        {profiles.length > 0 && stories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === "all" ? "bg-amber-500/20 border border-amber-400/40 text-amber-300" : "border border-purple-500/20 text-slate-400 hover:text-white"
              }`}
            >
              All Stories
            </button>
            {profiles.map((p) => (
              <button
                key={p.profile_id}
                onClick={() => setFilter(p.profile_id)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filter === p.profile_id ? "bg-amber-500/20 border border-amber-400/40 text-amber-300" : "border border-purple-500/20 text-slate-400 hover:text-white"
                }`}
              >
                {p.name}&apos;s Stories
              </button>
            ))}
          </div>
        )}

        {/* Story grid */}
        {filteredStories.length === 0 ? (
          <div data-testid={LIBRARY.emptyState} className="glass-card rounded-2xl p-16 text-center">
            <Moon size={36} className="text-purple-400 mx-auto mb-5" />
            <h3 className="font-serif text-xl font-semibold text-white mb-2">No stories here yet</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              Generate your first magical bedtime adventure and it will appear in your library automatically.
            </p>
            <Link
              to="/#generator"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold"
            >
              <Sparkles size={14} /> Create a Story
            </Link>
          </div>
        ) : (
          <div data-testid={LIBRARY.storyGrid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((s, i) => (
              <motion.div
                key={s.story_id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.06, 0.4), duration: 0.4 }}
                className="relative"
              >
                <Link
                  to={`/story/${s.story_id}`}
                  data-testid={`${LIBRARY.storyCard}-${s.story_id}`}
                  className="block glass-card rounded-2xl overflow-hidden group hover:border-amber-400/40 border border-transparent transition-all duration-300"
                >
                  <div className="h-44 relative overflow-hidden">
                    {s.cover_index != null ? (
                      <img
                        src={`${API}/stories/${s.story_id}/illustrations/${s.cover_index}`}
                        alt={s.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(109,40,217,0.25), rgba(245,158,11,0.15))" }}>
                        {s.generation_status === "generating" ? (
                          <Loader2 size={28} className="text-amber-300/70 animate-spin" />
                        ) : (
                          <Moon size={32} className="text-amber-300/60" />
                        )}
                      </div>
                    )}
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-[10px] font-mono uppercase tracking-wider text-amber-300">
                      {themeLabel(s.theme)}
                    </div>
                  </div>
                  <div className="p-5">
                    {s.generation_status === "generating" ? (
                      <div data-testid={`story-generating-badge-${s.story_id}`} className="flex items-center gap-2 text-amber-300">
                        <Loader2 size={14} className="animate-spin" />
                        <h3 className="font-serif text-base font-semibold">Weaving your story...</h3>
                      </div>
                    ) : s.generation_status === "failed" ? (
                      <h3 className="font-serif text-base font-semibold text-red-300">Story generation failed</h3>
                    ) : (
                      <h3 className="font-serif text-lg font-bold text-white leading-snug group-hover:text-amber-200 transition-colors line-clamp-2">
                        {s.title}
                      </h3>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      For {s.child_name}{s.sibling_name ? ` & ${s.sibling_name}` : `, age ${s.child_age}`}
                      {s.word_count ? ` · ~${Math.ceil(s.word_count / 130)} min read` : ""}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""}
                    </p>
                  </div>
                </Link>
                {/* Playlist controls live OUTSIDE the Link (avoids nested interactive content + stacking bugs) */}
                <button
                  data-testid={`queue-toggle-${s.story_id}`}
                  onClick={() => {
                    setNewPlName("");
                    setPlMenuError("");
                    setOpenMenuFor(openMenuFor === s.story_id ? null : s.story_id);
                  }}
                  title="Add to a playlist"
                  className={`absolute top-2 right-2 z-40 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors ${
                    inAnyPlaylist(s.story_id)
                      ? "bg-amber-500 text-black"
                      : "bg-black/50 text-slate-300 hover:bg-black/70 hover:text-amber-300"
                  }`}
                >
                  {inAnyPlaylist(s.story_id) ? <Check size={16} /> : <ListPlus size={16} />}
                </button>
                <button
                  data-testid={`delete-story-btn-${s.story_id}`}
                  onClick={() => setConfirmDelete(s)}
                  title="Delete story"
                  className="absolute top-2 right-[52px] z-40 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-sm bg-black/50 text-slate-300 hover:bg-red-500/80 hover:text-white transition-colors"
                >
                  <Trash2 size={15} />
                </button>
                {openMenuFor === s.story_id && (
                  <div
                    data-testid={`playlist-menu-${s.story_id}`}
                    className="absolute top-14 right-2 z-40 w-56 rounded-xl bg-[#161230] border border-purple-500/30 shadow-2xl shadow-black/60 p-2"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 px-2 py-1">Add to playlist</p>
                    {playlistsList.map((pl) => (
                      <button
                        key={pl.playlist_id}
                        data-testid={`playlist-menu-item-${pl.playlist_id}`}
                        onClick={() => toggleInPlaylist(pl, s.story_id)}
                        className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-slate-300 hover:bg-white/5 transition-colors"
                      >
                        <span className="truncate">{pl.name}</span>
                        {pl.story_ids.includes(s.story_id) && <Check size={12} className="text-amber-400 shrink-0" />}
                      </button>
                    ))}
                    <form onSubmit={(e) => createPlaylistWithStory(e, s.story_id)} className="flex items-center gap-1 mt-1 px-1">
                      <input
                        data-testid="new-playlist-input"
                        value={newPlName}
                        onChange={(e) => setNewPlName(e.target.value)}
                        placeholder="New playlist..."
                        className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-white/5 border border-purple-500/20 text-white text-xs placeholder-slate-600 focus:outline-none focus:border-amber-400/40"
                      />
                      <button type="submit" data-testid="new-playlist-save" className="p-1.5 text-amber-400 hover:text-amber-300">
                        <Plus size={13} />
                      </button>
                    </form>
                    {plMenuError && <p data-testid="playlist-menu-error" className="text-[10px] text-red-400 px-2 pt-1">{plMenuError}</p>}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Trash bin (soft-deleted stories, 7-day recovery) */}
        {trash.length > 0 && (
          <section data-testid="trash-section" className="mt-12">
            <button
              data-testid="trash-toggle"
              onClick={() => setTrashOpen((v) => !v)}
              className="w-full flex items-center gap-3 py-4 border-t border-purple-500/15 text-left"
            >
              <Trash2 size={14} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-400 tracking-wider uppercase">
                Trash · {trash.length} {trash.length === 1 ? "story" : "stories"}
              </span>
              <span className="text-xs text-slate-600 hidden sm:inline">Undo any story within 7 days</span>
              <span className="ml-auto text-slate-500">
                {trashOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </button>
            <AnimatePresence>
              {trashOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  {trashError && (
                    <p data-testid="trash-error" className="text-xs text-red-400 pt-3">{trashError}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                    {trash.map((s) => (
                      <div
                        key={s.story_id}
                        data-testid={`trash-item-${s.story_id}`}
                        className="glass-card rounded-xl p-4 flex items-start gap-3 opacity-90"
                      >
                        <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-white/5">
                          {s.cover_index != null ? (
                            <img
                              src={`${API}/stories/${s.story_id}/illustrations/${s.cover_index}`}
                              alt={s.title}
                              className="w-full h-full object-cover grayscale"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Moon size={16} className="text-slate-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-300 truncate">{s.title}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            For {s.child_name}{s.sibling_name ? ` & ${s.sibling_name}` : ""}
                          </p>
                          <p className="text-[11px] text-amber-300/70 mt-1">
                            {daysLeft(s.purge_at)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              data-testid={`trash-restore-${s.story_id}`}
                              onClick={() => handleRestore(s.story_id)}
                              disabled={restoringId === s.story_id}
                              className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-400/30 text-amber-300 text-[11px] font-medium hover:bg-amber-500/10 transition-colors disabled:opacity-50"
                            >
                              {restoringId === s.story_id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                              Restore
                            </button>
                            <button
                              data-testid={`trash-purge-${s.story_id}`}
                              onClick={() => handlePurge(s.story_id)}
                              disabled={purgingId === s.story_id}
                              className="text-[11px] text-slate-500 hover:text-red-400 px-2 py-1 transition-colors disabled:opacity-50"
                            >
                              {purgingId === s.story_id ? "Deleting..." : "Delete forever"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        )}

        {/* Plan management */}
        <ManagePlan user={user} />
      </div>

      {/* Delete story confirm dialog */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
            data-testid="delete-story-modal"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deletingId && setConfirmDelete(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative glass-card rounded-3xl p-8 max-w-md w-full text-center border border-red-400/20"
            >
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-400/30 flex items-center justify-center mx-auto mb-5">
                <Trash2 size={20} className="text-red-300" />
              </div>
              <h3 className="font-serif text-xl font-bold text-white mb-2">Move to trash?</h3>
              <p className="text-sm text-slate-400 mb-1 line-clamp-2">
                &ldquo;{confirmDelete.title}&rdquo;
              </p>
              <p className="text-xs text-slate-500 mb-6">
                You&apos;ll have 7 days to bring it back from the Trash before it&apos;s gone forever.
              </p>
              {deleteError && <p className="text-xs text-red-400 mb-3">{deleteError}</p>}
              <div className="flex items-center justify-center gap-3">
                <button
                  data-testid="delete-story-cancel"
                  onClick={() => { setConfirmDelete(null); setDeleteError(""); }}
                  disabled={!!deletingId}
                  className="px-5 py-2.5 rounded-full border border-purple-500/30 text-slate-300 text-xs font-semibold hover:text-white transition-colors disabled:opacity-50"
                >
                  Keep it
                </button>
                <button
                  data-testid="delete-story-confirm"
                  onClick={handleDeleteStory}
                  disabled={!!deletingId}
                  className="px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {deletingId && <Loader2 size={12} className="animate-spin" />}
                  {deletingId ? "Moving..." : "Yes, move to trash"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Undo toast (after soft-delete) */}
      <AnimatePresence>
        {undoStory && (
          <motion.div
            data-testid="undo-toast"
            initial={{ opacity: 0, y: 40, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 40, x: "-50%" }}
            className="fixed bottom-24 left-1/2 z-[55] w-[calc(100vw-2rem)] max-w-md sm:w-auto"
          >
            <div className="glass-card rounded-full pl-5 pr-2 py-2 flex items-center gap-3 sm:gap-4 shadow-2xl shadow-black/50 border border-purple-500/30 w-full sm:w-auto">
              <span className="text-sm text-slate-300 flex-1 min-w-0 truncate">
                <span className="text-amber-300 font-semibold">&ldquo;{undoStory.title}&rdquo;</span>
                <span className="text-slate-500"> · moved to trash</span>
              </span>
              <button
                data-testid="undo-restore-btn"
                onClick={() => handleRestore(undoStory.story_id)}
                className="flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-white/10 hover:bg-white/15 border border-amber-400/30 text-amber-300 text-xs font-bold transition-all shrink-0"
              >
                <Undo2 size={12} /> Undo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Playlists bar */}
      <AnimatePresence>
        {queuedTotal > 0 && (
          <motion.div
            data-testid="playlist-bar"
            initial={{ opacity: 0, y: 40, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 40, x: "-50%" }}
            className="fixed bottom-5 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-md sm:w-auto"
          >
            <div className="glass-card rounded-full pl-5 pr-2 py-2 flex items-center gap-3 sm:gap-4 shadow-2xl shadow-purple-900/40 border border-amber-400/20 w-full sm:w-auto">
              <span className="text-sm text-slate-300 flex-1 min-w-0 truncate">
                <span className="text-amber-300 font-semibold">{firstNonEmpty?.name}</span> · {firstNonEmpty?.story_ids.length} {firstNonEmpty?.story_ids.length === 1 ? "story" : "stories"}
                {playlistsList.filter((p) => p.story_ids.length > 0).length > 1 &&
                  ` · +${playlistsList.filter((p) => p.story_ids.length > 0).length - 1} more`}
              </span>
              <button
                data-testid="playlist-play-bar-btn"
                onClick={() => navigate(`/playlist?pl=${firstNonEmpty?.playlist_id}`)}
                className="flex items-center gap-2 min-h-[44px] px-5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-xs font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all shrink-0"
              >
                <Play size={12} /> Play
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
